import { useState } from "react";
import { useLoginMutation } from "../api/auth";
import { useNavigate } from "react-router-dom";
import { useEstablishSession } from "../hooks/useEstablishSession";
import { Card, Label, TextInput, Button } from "flowbite-react";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export default function LoginPage() {
  const [emailOrUsername, setemailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [login] = useLoginMutation();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");

  const { establishSession, setIsAuthenticated } = useEstablishSession();

  const handleLogin = async (username: string, password: string) => {
    setLoading(true);
    setLoginError(null);

    try {
      const response = await login({
        agent_email_or_username: username,
        agent_password: password,
      }).unwrap();

      // The MSRP digest runs on the user-typed plaintext, not on
      // `response.agent_password` — that field is a stored HA1 ("h1_<hash>")
      // and feeding it back in would double-hash. HA1 is recomputed in
      // msrpAuth from this plaintext plus the realm SCont returns in the 401.
      const result = await establishSession(response, { username, secret: password });

      if (result === "reloading") return;

      navigate("/");
      finalizeLogin(true);
    } catch (error: any) {
      console.error("Error during login process:", error);
      toast.error(t("login.toast.error"));
      setLoginError(t("login.toast.error"));
      finalizeLogin(false);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const finalizeLogin = (isSuccess: boolean) => {
    setLoading(false);
    if (!isSuccess) setLoginError(t("login.error"));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      toast.error(t("login.toast.passwordEmptyError"));
      return;
    }
    if (!emailOrUsername.trim()) {
      toast.error(t("login.toast.emailEmptyError"));
      return;
    }
    handleLogin(emailOrUsername, password);
  };

  return (
    <div className="flex flex-col items-center justify-center px-6 lg:h-screen lg:gap-y-12">
      <div className="absolute bottom-5 right-5 text-xs text-gray-500 dark:text-gray-400">
        version 2.9.0
      </div>
      <a href="/" className="my-6 flex items-center gap-x-1 lg:my-0">
        <img alt="ICONT" src="/images/icont.png" className="mr-3 h-12" />
      </a>
      <Card
        horizontal
        imgSrc="/images/loginpage.png"
        imgAlt="ICONT"
        className="w-full md:max-w-[1024px] md:[&>*]:w-full md:[&>*]:p-16 [&>img]:hidden md:[&>img]:w-96 md:[&>img]:p-0 lg:[&>img]:block"
      >
        <h1 className="mb-3 text-2xl font-bold dark:text-white md:text-3xl">
          {t("login.title")}
        </h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4 flex flex-col gap-y-3">
            <Label htmlFor="username">{tCommon("form.userName")}</Label>
            <TextInput
              id="username"
              name="username"
              placeholder={tCommon("form.emailOrUsernamePlaceholder")}
              type="text"
              value={emailOrUsername}
              onChange={(e) => setemailOrUsername(e.target.value)}
            />
          </div>
          <div className="mb-6 flex flex-col gap-y-3">
            <Label htmlFor="password">{tCommon("form.password")}</Label>
            <div className="relative">
              <TextInput
                id="password"
                name="password"
                placeholder={tCommon("form.password")}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400"
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showPassword ? <HiEyeOff className="h-5 w-5" /> : <HiEye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          {loginError && (
            <p className="mb-4 text-center text-red-500">{loginError}</p>
          )}
          <div className="mb-6">
            <Button
              type="submit"
              className="bg-gradient-to-br from-purple-600 to-blue-500 text-white hover:bg-gradient-to-bl focus:ring-blue-300 dark:focus:ring-blue-800"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="mr-2 h-5 w-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    ></path>
                  </svg>
                  {t("login.isSubmittingText")}
                </div>
              ) : (
                t("login.submitButton")
              )}
            </Button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-300">
            {t("login.notRegistered")}&nbsp;
            <button
              onClick={() => navigate("/signup")}
              className="text-primary-600 dark:text-primary-300"
            >
              {t("login.createAccount")}
            </button>
          </p>
        </form>
      </Card>
    </div>
  );
}